import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

import { supabase } from '../../../lib/supabase';

const DetailedAnalyticsPanel = ({ dateRange, campaignId }) => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('technical');
  const [analyticsData, setAnalyticsData] = useState({
    technical: {},
    marketing: {},
    behavior: {},
    rawScans: []
  });

  useEffect(() => {
    loadDetailedAnalytics();
  }, [dateRange, campaignId]);

  const loadDetailedAnalytics = async () => {
    setLoading(true);
    try {
      // Build date filter
      let dateFilter = '';
      if (dateRange && dateRange?.start && dateRange?.end) {
        const startDate = new Date(dateRange.start)?.toISOString();
        const endDate = new Date(dateRange.end)?.toISOString();
        dateFilter = `scanned_at.gte.${startDate},scanned_at.lte.${endDate}`;
      }

      // Get QR codes for campaign filtering
      let qrCodeIds = [];
      if (campaignId && campaignId !== 'all') {
        const { data: campaignQRs } = await supabase
          ?.from('qr_codes')
          ?.select('id')
          ?.eq('campaign_id', campaignId);
        qrCodeIds = campaignQRs?.map(qr => qr?.id) || [];
      }

      // Build the query for detailed scan data
      let query = supabase
        ?.from('qr_scans')
        ?.select(`
          *,
          qr_codes!inner (
            name,
            qr_type,
            campaign_id
          )
        `);

      // Apply filters
      if (dateFilter) {
        const [startDate, endDate] = [dateRange?.start, dateRange?.end];
        query = query
          ?.gte('scanned_at', new Date(startDate)?.toISOString())
          ?.lte('scanned_at', new Date(endDate)?.toISOString());
      }

      if (qrCodeIds?.length > 0) {
        query = query?.in('qr_code_id', qrCodeIds);
      }

      const { data: scansData, error } = await query?.order('scanned_at', { ascending: false })?.limit(1000);

      if (error) throw error;

      // Process the data into categories
      const processedData = processAnalyticsData(scansData || []);
      setAnalyticsData(processedData);
    } catch (error) {
      console.log('Failed to load detailed analytics:', error);
      setAnalyticsData({
        technical: {},
        marketing: {},
        behavior: {},
        rawScans: []
      });
    } finally {
      setLoading(false);
    }
  };

  const processAnalyticsData = (scans) => {
    // Technical Environment Processing
    const technical = {
      browsers: {},
      operatingSystems: {},
      devices: {},
      userAgents: [],
      ipAddresses: new Set(),
      totalUniqueIPs: 0
    };

    // Marketing Attribution Processing
    const marketing = {
      utmSources: {},
      utmMediums: {},
      utmCampaigns: {},
      referrers: {},
      conversions: {
        total: 0,
        value: 0,
        rate: 0
      }
    };

    // User Behavior Processing
    const behavior = {
      scanStatus: {},
      uniqueUsers: 0,
      repeatUsers: 0,
      timeAnalysis: {},
      geographicData: {}
    };

    scans?.forEach(scan => {
      // Technical Environment
      if (scan?.browser_name) {
        technical.browsers[scan?.browser_name] = (technical?.browsers?.[scan?.browser_name] || 0) + 1;
      }
      
      if (scan?.os_name) {
        technical.operatingSystems[scan?.os_name] = (technical?.operatingSystems?.[scan?.os_name] || 0) + 1;
      }
      
      if (scan?.device_type) {
        technical.devices[scan?.device_type] = (technical?.devices?.[scan?.device_type] || 0) + 1;
      }
      
      if (scan?.user_agent) {
        technical?.userAgents?.push({
          agent: scan?.user_agent,
          timestamp: scan?.scanned_at,
          device: scan?.device_type
        });
      }
      
      if (scan?.ip_address) {
        technical?.ipAddresses?.add(scan?.ip_address);
      }

      // Marketing Attribution
      if (scan?.utm_source) {
        marketing.utmSources[scan?.utm_source] = (marketing?.utmSources?.[scan?.utm_source] || 0) + 1;
      }
      
      if (scan?.utm_medium) {
        marketing.utmMediums[scan?.utm_medium] = (marketing?.utmMediums?.[scan?.utm_medium] || 0) + 1;
      }
      
      if (scan?.utm_campaign) {
        marketing.utmCampaigns[scan?.utm_campaign] = (marketing?.utmCampaigns?.[scan?.utm_campaign] || 0) + 1;
      }
      
      if (scan?.referrer) {
        marketing.referrers[scan?.referrer] = (marketing?.referrers?.[scan?.referrer] || 0) + 1;
      }
      
      if (scan?.converted) {
        marketing.conversions.total += 1;
        marketing.conversions.value += parseFloat(scan?.conversion_value || 0);
      }

      // User Behavior
      if (scan?.scan_status) {
        behavior.scanStatus[scan?.scan_status] = (behavior?.scanStatus?.[scan?.scan_status] || 0) + 1;
      }
      
      if (scan?.is_unique_user) {
        behavior.uniqueUsers += 1;
      } else {
        behavior.repeatUsers += 1;
      }
      
      // Time analysis
      const hour = new Date(scan?.scanned_at)?.getHours();
      behavior.timeAnalysis[hour] = (behavior?.timeAnalysis?.[hour] || 0) + 1;
      
      // Geographic
      if (scan?.country_name) {
        behavior.geographicData[scan?.country_name] = (behavior?.geographicData?.[scan?.country_name] || 0) + 1;
      }
    });

    // Finalize calculations
    technical.totalUniqueIPs = technical?.ipAddresses?.size;
    technical.userAgents = technical?.userAgents?.slice(0, 50); // Limit for performance

    marketing.conversions.rate = scans?.length > 0 ? 
      ((marketing?.conversions?.total / scans?.length) * 100)?.toFixed(2) : '0.00';

    return {
      technical,
      marketing,
      behavior,
      rawScans: scans?.slice(0, 100) // Latest 100 for detailed view
    };
  };

  const renderTechnicalTab = () => (
    <div className="space-y-6">
      {/* Browser Breakdown */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
          <Icon name="Globe" size={18} className="mr-2 text-blue-600" />
          Browser Distribution
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.entries(analyticsData?.technical?.browsers || {})?.map(([browser, count]) => (
            <div key={browser} className="flex items-center justify-between">
              <span className="text-sm text-gray-700">{browser}</span>
              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm font-medium">
                {count}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Operating Systems */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
          <Icon name="Monitor" size={18} className="mr-2 text-green-600" />
          Operating Systems
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.entries(analyticsData?.technical?.operatingSystems || {})?.map(([os, count]) => (
            <div key={os} className="flex items-center justify-between">
              <span className="text-sm text-gray-700">{os}</span>
              <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-sm font-medium">
                {count}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Device Types */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
          <Icon name="Smartphone" size={18} className="mr-2 text-purple-600" />
          Device Breakdown
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.entries(analyticsData?.technical?.devices || {})?.map(([device, count]) => (
            <div key={device} className="flex items-center justify-between">
              <span className="text-sm text-gray-700 capitalize">{device}</span>
              <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-sm font-medium">
                {count}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* IP Address Stats */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
          <Icon name="MapPin" size={18} className="mr-2 text-red-600" />
          Network Information
        </h4>
        <div className="text-sm text-gray-700">
          <p><span className="font-medium">Unique IP Addresses:</span> {analyticsData?.technical?.totalUniqueIPs}</p>
          <p className="mt-1"><span className="font-medium">User Agents Recorded:</span> {analyticsData?.technical?.userAgents?.length}</p>
        </div>
      </div>
    </div>
  );

  const renderMarketingTab = () => (
    <div className="space-y-6">
      {/* UTM Source Analysis */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
          <Icon name="ExternalLink" size={18} className="mr-2 text-blue-600" />
          UTM Sources
        </h4>
        {Object.keys(analyticsData?.marketing?.utmSources || {})?.length > 0 ? (
          <div className="space-y-2">
            {Object.entries(analyticsData?.marketing?.utmSources || {})?.map(([source, count]) => (
              <div key={source} className="flex items-center justify-between">
                <span className="text-sm text-gray-700">{source}</span>
                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm font-medium">
                  {count} scans
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No UTM source data available</p>
        )}
      </div>

      {/* UTM Medium Analysis */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
          <Icon name="Radio" size={18} className="mr-2 text-green-600" />
          UTM Mediums
        </h4>
        {Object.keys(analyticsData?.marketing?.utmMediums || {})?.length > 0 ? (
          <div className="space-y-2">
            {Object.entries(analyticsData?.marketing?.utmMediums || {})?.map(([medium, count]) => (
              <div key={medium} className="flex items-center justify-between">
                <span className="text-sm text-gray-700">{medium}</span>
                <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-sm font-medium">
                  {count} scans
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No UTM medium data available</p>
        )}
      </div>

      {/* UTM Campaign Analysis */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
          <Icon name="Target" size={18} className="mr-2 text-purple-600" />
          UTM Campaigns
        </h4>
        {Object.keys(analyticsData?.marketing?.utmCampaigns || {})?.length > 0 ? (
          <div className="space-y-2">
            {Object.entries(analyticsData?.marketing?.utmCampaigns || {})?.map(([campaign, count]) => (
              <div key={campaign} className="flex items-center justify-between">
                <span className="text-sm text-gray-700">{campaign}</span>
                <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-sm font-medium">
                  {count} scans
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No UTM campaign data available</p>
        )}
      </div>

      {/* Referrer Analysis */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
          <Icon name="ArrowLeft" size={18} className="mr-2 text-orange-600" />
          Referrer Sources
        </h4>
        {Object.keys(analyticsData?.marketing?.referrers || {})?.length > 0 ? (
          <div className="space-y-2">
            {Object.entries(analyticsData?.marketing?.referrers || {})?.slice(0, 10)?.map(([referrer, count]) => (
              <div key={referrer} className="flex items-center justify-between">
                <span className="text-sm text-gray-700 truncate max-w-xs" title={referrer}>
                  {referrer}
                </span>
                <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded text-sm font-medium">
                  {count}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No referrer data available</p>
        )}
      </div>

      {/* Conversion Tracking */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
          <Icon name="TrendingUp" size={18} className="mr-2 text-red-600" />
          Conversion Analytics
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">
              {analyticsData?.marketing?.conversions?.total || 0}
            </div>
            <div className="text-sm text-gray-600">Total Conversions</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">
              ${analyticsData?.marketing?.conversions?.value?.toFixed(2) || '0.00'}
            </div>
            <div className="text-sm text-gray-600">Conversion Value</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              {analyticsData?.marketing?.conversions?.rate || 0}%
            </div>
            <div className="text-sm text-gray-600">Conversion Rate</div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderBehaviorTab = () => (
    <div className="space-y-6">
      {/* Scan Status */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
          <Icon name="CheckCircle" size={18} className="mr-2 text-green-600" />
          Scan Status Distribution
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Object.entries(analyticsData?.behavior?.scanStatus || {})?.map(([status, count]) => (
            <div key={status} className="text-center">
              <div className={`text-2xl font-bold ${
                status === 'success' ? 'text-green-600' :
                status === 'failed' ? 'text-red-600' : 'text-yellow-600'
              }`}>
                {count}
              </div>
              <div className="text-sm text-gray-600 capitalize">{status}</div>
            </div>
          ))}
        </div>
      </div>

      {/* User Type Analysis */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
          <Icon name="Users" size={18} className="mr-2 text-blue-600" />
          User Behavior
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              {analyticsData?.behavior?.uniqueUsers || 0}
            </div>
            <div className="text-sm text-gray-600">Unique Users</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">
              {analyticsData?.behavior?.repeatUsers || 0}
            </div>
            <div className="text-sm text-gray-600">Repeat Users</div>
          </div>
        </div>
      </div>

      {/* Time Analysis */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
          <Icon name="Clock" size={18} className="mr-2 text-orange-600" />
          Hourly Activity Pattern
        </h4>
        <div className="grid grid-cols-6 md:grid-cols-12 gap-2">
          {Array.from({ length: 24 }, (_, hour) => {
            const count = analyticsData?.behavior?.timeAnalysis?.[hour] || 0;
            const maxCount = Math.max(...Object.values(analyticsData?.behavior?.timeAnalysis || {}));
            const intensity = maxCount > 0 ? (count / maxCount) : 0;
            
            return (
              <div key={hour} className="text-center">
                <div 
                  className={`h-8 rounded mb-1 ${
                    intensity > 0.7 ? 'bg-orange-500' :
                    intensity > 0.4 ? 'bg-orange-300' :
                    intensity > 0.1 ? 'bg-orange-100' : 'bg-gray-100'
                  }`}
                  title={`${hour}:00 - ${count} scans`}
                ></div>
                <div className="text-xs text-gray-600">{hour}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Geographic Distribution */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
          <Icon name="Globe" size={18} className="mr-2 text-indigo-600" />
          Geographic Distribution
        </h4>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {Object.entries(analyticsData?.behavior?.geographicData || {})?.map(([country, count]) => (
            <div key={country} className="flex items-center justify-between">
              <span className="text-sm text-gray-700">{country}</span>
              <span className="bg-indigo-100 text-indigo-800 px-2 py-1 rounded text-sm font-medium">
                {count} scans
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const tabs = [
    { id: 'technical', name: 'Technical Environment', icon: 'Monitor' },
    { id: 'marketing', name: 'Marketing Attribution', icon: 'Target' },
    { id: 'behavior', name: 'User Behavior', icon: 'Users' }
  ];

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin">
            <Icon name="Loader2" size={24} className="text-gray-400" />
          </div>
          <span className="ml-2 text-gray-600">Loading detailed analytics...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <Icon name="BarChart3" size={20} className="mr-2 text-blue-600" />
          Detailed Analytics Dashboard
        </h3>
        <p className="text-sm text-gray-600 mt-1">
          Comprehensive view of technical environment, marketing attribution, and user behavior data
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex space-x-4">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === tab?.id
                  ? 'bg-blue-100 text-blue-700' :'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              <Icon name={tab?.icon} size={16} className="mr-2" />
              {tab?.name}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="px-6 py-6">
        {activeTab === 'technical' && renderTechnicalTab()}
        {activeTab === 'marketing' && renderMarketingTab()}
        {activeTab === 'behavior' && renderBehaviorTab()}
      </div>

      {/* Footer */}
      <div className="px-6 py-4 bg-gray-50 rounded-b-xl">
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span>
            Data from {analyticsData?.rawScans?.length || 0} recent scan records
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={loadDetailedAnalytics}
            className="text-blue-600 hover:text-blue-700"
          >
            <Icon name="RefreshCw" size={14} className="mr-1" />
            Refresh
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DetailedAnalyticsPanel;